import { motion } from "framer-motion";
import { Star, MapPin, Phone, Clock } from "lucide-react";
import { COMPANY } from "@/lib/site";

// NOTE: Sample testimonials — replace with real customer reviews when available.
const REVIEWS = [
  {
    quote:
      "Trey's moved a time-sensitive flatbed load for us on short notice and kept us updated the whole way. Rare to find that kind of communication.",
    name: "Operations Manager",
    org: "Gulf Coast Steel Supply",
  },
  {
    quote:
      "Reliable capacity every time we call, and honest rates. They've become our go-to carrier for Texas lanes.",
    name: "Logistics Coordinator",
    org: "Lone Star Building Products",
  },
  {
    quote:
      "Professional, on-time, and the driver treated our freight like it was his own. Exactly what you want in a carrier.",
    name: "Warehouse Director",
    org: "Bayou City Distribution",
  },
];

const MAP_SRC =
  "https://www.google.com/maps?q=15015+Westheimer+Pkwy+Suite+310+Houston+TX+77082&output=embed";

export default function MapReviews() {
  return (
    <section id="reviews" data-testid="map-reviews" className="border-t border-white/10">
      <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end"
        >
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-brand">Shippers Trust Us</span>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
              Proof on
              <br />
              the pavement
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-brand text-brand" />
              ))}
            </div>
            <span className="text-sm text-[#a1a1a8]">Rated by the shippers we haul for</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {REVIEWS.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`review-${i}`}
              className="flex flex-col justify-between border border-white/10 bg-surface p-8 transition-colors hover:bg-surfaceHover"
            >
              <div className="mb-6 flex">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-brand text-brand" />
                ))}
              </div>
              <p className="text-base leading-relaxed text-[#e4e4e7]">"{r.quote}"</p>
              <div className="mt-8 border-t border-white/10 pt-4">
                <div className="font-display text-lg font-bold uppercase tracking-tight">{r.name}</div>
                <div className="text-sm text-[#a1a1a8]">{r.org}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[1.5fr_1fr]">
          <div className="relative min-h-[360px] overflow-hidden border border-white/10">
            <iframe
              title="Trey's Trucking Logistics — Houston HQ"
              src={MAP_SRC}
              data-testid="hq-map"
              className="h-full w-full grayscale-[0.2] contrast-125"
              style={{ border: 0, minHeight: 360, filter: "invert(0.9) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="flex flex-col justify-between gap-6 border border-white/10 bg-surface p-8 md:p-10">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-brand">Our Yard</span>
              <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight">
                Based in Houston, Texas
              </h3>
            </div>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-brand" />
                <span className="text-[#d4d4d8]">
                  {COMPANY.address}
                  <br />
                  {COMPANY.city}
                </span>
              </li>
              <li className="flex items-center gap-4">
                <Phone className="h-5 w-5 flex-shrink-0 text-brand" />
                <a href={COMPANY.phoneHref} className="text-[#d4d4d8] hover:text-brand">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-center gap-4">
                <Clock className="h-5 w-5 flex-shrink-0 text-brand" />
                <span className="text-[#d4d4d8]">Dispatch available 24/7</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
